// MyFundraisingPages — donor portal peer-to-peer fundraising pages list
// (CHG-1521 wave 3).
//
// /my-giving/fundraising. Consumes GET /api/v1/my-giving/peer-pages
// (multi-org peer-page list). Wave 3 ships read + inline pause/resume
// row actions; create/edit/delete is deferred to a wave 4 detail page.

import { useState, type ReactNode } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { ListPage, type ListPageConfig } from "../components/composition/ListPage";
import { ProgressBar } from "../components/composition/ProgressBar";
import { useFormatters } from "../contexts/IntlContext";
import { mePeerPagesApi, type MyPeerPage } from "../api/me-peer-pages";
import { hasNativeCurrencyPrecision, minorUnitsToMajor } from "@generated/currency-decimals";

const STATUS_LABEL: Record<string, string> = {
  draft: "Draft",
  published: "Published",
  paused: "Paused",
  closed: "Closed",
};

function statusColumn(row: MyPeerPage): ReactNode {
  return STATUS_LABEL[row.status] ?? row.status;
}

function ProgressCell({ row }: { row: MyPeerPage }) {
  const { formatCurrency } = useFormatters();
  const currency = row.currency;
  const goal = row.goal_amount_minor_units;
  const raised = row.raised_minor_units;
  const formatCampaignAmount = (amount: number, denomination: unknown) => {
    if (!hasNativeCurrencyPrecision(denomination) || !Number.isSafeInteger(amount)) return "Amount unavailable";
    const formatterAmount = minorUnitsToMajor(amount, denomination) * 100;
    if (Math.abs(formatterAmount) > Number.MAX_SAFE_INTEGER) return "Amount unavailable";
    return `${denomination} ${formatCurrency(formatterAmount, denomination)}`;
  };
  if (!hasNativeCurrencyPrecision(currency) || !Number.isSafeInteger(goal) || !Number.isSafeInteger(raised)) {
    return <span>Campaign currency or amount unavailable</span>;
  }
  const foreignAmounts = row.raised_by_currency && Object.entries(row.raised_by_currency)
    .filter(([denomination]) => denomination !== currency)
    .sort(([left], [right]) => left.localeCompare(right));
  const otherReceipts = foreignAmounts?.length ? (
    <div>Also received: {foreignAmounts.map(([denomination, amount]) => formatCampaignAmount(amount, denomination)).join(" · ")}</div>
  ) : null;
  if (goal <= 0) {
    return (
      <div style={{ fontSize: 13, color: "var(--color-text-muted)" }}>
        Raised: <strong>{formatCampaignAmount(raised, currency)}</strong>{" "}
        <span style={{ opacity: 0.6 }}>(no goal set)</span>
        {otherReceipts}
      </div>
    );
  }
  const pct = Math.min(100, Math.round((raised / goal) * 100));
  return (
    <div style={{ minWidth: 180 }}>
      <ProgressBar
        value={pct}
        max={100}
        tone="g-mygiving"
        valueFormatted={`${formatCampaignAmount(raised, currency)} / ${formatCampaignAmount(goal, currency)} (${pct}%)`}
      />
      {otherReceipts}
    </div>
  );
}

function ActionCell({ row }: { row: MyPeerPage }) {
  const queryClient = useQueryClient();
  const [pending, setPending] = useState<"pause" | "resume" | null>(null);
  async function handlePause() {
    setPending("pause");
    try {
      await mePeerPagesApi.pauseMyPeerPage(row.id);
      queryClient.invalidateQueries({ queryKey: ["me", "peer-pages", "list"] });
    } finally {
      setPending(null);
    }
  }
  async function handleResume() {
    setPending("resume");
    try {
      await mePeerPagesApi.resumeMyPeerPage(row.id);
      queryClient.invalidateQueries({ queryKey: ["me", "peer-pages", "list"] });
    } finally {
      setPending(null);
    }
  }
  if (row.status === "published") {
    return (
      <button
        type="button"
        onClick={handlePause}
        disabled={pending !== null}
        style={{
          fontSize: 12,
          padding: "4px 10px",
          border: "1px solid var(--color-border)",
          borderRadius: 4,
          background: "var(--color-surface)",
          color: "var(--color-text)",
          cursor: pending ? "wait" : "pointer",
        }}
        aria-label={`Pause ${row.title}`}
      >
        {pending === "pause" ? "Pausing…" : "Pause"}
      </button>
    );
  }
  if (row.status === "paused") {
    return (
      <button
        type="button"
        onClick={handleResume}
        disabled={pending !== null}
        style={{
          fontSize: 12,
          padding: "4px 10px",
          border: "1px solid var(--color-border)",
          borderRadius: 4,
          background: "var(--accent, #0066cc)",
          color: "white",
          cursor: pending ? "wait" : "pointer",
        }}
        aria-label={`Resume ${row.title}`}
      >
        {pending === "resume" ? "Resuming…" : "Resume"}
      </button>
    );
  }
  return <span style={{ fontSize: 13, color: "var(--color-text-muted)" }}>—</span>;
}

const myFundraisingPagesListConfig: ListPageConfig<MyPeerPage> = {
  title: "My fundraising",
  subtitle:
    "Peer-to-peer fundraising pages you've created. Track progress, pause or resume each page.",
  queryKey: ["me", "peer-pages", "list"] as const,
  queryFn: ({ page, pageSize, sortField, sortDirection, search }) =>
    mePeerPagesApi.listMyPeerPages({
      page,
      pageSize,
      sortField,
      sortDirection,
      search,
    }),
  defaultSort: { field: "created_at", direction: "desc" },
  pageSize: 25,
  emptyState: {
    title: "No fundraising pages yet",
    subtitle:
      "Pages you create to rally friends and family around campaigns will appear here. Speak to an organization you support about starting one.",
  },
  columns: [
    {
      field: "title",
      label: "Page",
      type: "text",
    },
    {
      field: "organization_name",
      label: "Organization",
      type: "text",
    },
    {
      field: "campaign_name",
      label: "Campaign",
      type: "custom",
      render: (row) => row.campaign_name ?? "—",
    },
    {
      field: "raised_minor_units",
      label: "Progress",
      type: "custom",
      render: (row) => <ProgressCell row={row} />,
    },
    {
      field: "donor_count",
      label: "Donors",
      type: "number",
      sortable: true,
    },
    {
      field: "status",
      label: "Status",
      type: "custom",
      sortable: true,
      render: statusColumn,
    },
    {
      field: "id",
      label: "Actions",
      type: "custom",
      render: (row) => <ActionCell row={row} />,
    },
  ],
};

export function MyFundraisingPages() {
  return <ListPage config={myFundraisingPagesListConfig} />;
}
